import { httpAuth } from 'config/apiClient'
import { AppDispatch } from 'config/redux'
import endpoints from 'constants/endpoints'
import { RootState } from 'constants/interfaces'
import { isNumber } from 'lodash'
import { AnyAction } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { Campaign } from 'redux/reducers/campaigns'
import { getTokenFromCookie, getUTCOffset } from 'utils'

const BASE_URL = 'https://powersale.storee.ai/salesanalytics?'

export const actionTypes = {
  SET_CAMPAIGNS: 'SET_CAMPAIGNS',
  SET_CAMPAIGNS_LOADING: 'SET_CAMPAIGNS_LOADING',
  SET_IS_CAMPAIGN_CREATING: 'SET_IS_CAMPAIGN_CREATING',
  UPDATE_CAMPAIGN: 'UPDATE_CAMPAIGN',
  DELETE_CAMPAIGN: 'DELETE_CAMPAIGN',
}

export const setCampaigns = (campaigns: Campaign[]) => ({
  type: actionTypes.SET_CAMPAIGNS,
  payload: campaigns,
})

export const updateCampaign = (campaign: Campaign) => ({
  type: actionTypes.UPDATE_CAMPAIGN,
  payload: campaign,
})

export const setCampaignsLoading = (loading: boolean) => ({
  type: actionTypes.SET_CAMPAIGNS_LOADING,
  payload: loading,
})
export const setIsCampaignCreating = (loading: boolean) => ({
  type: actionTypes.SET_IS_CAMPAIGN_CREATING,
  payload: loading,
})

export const deleteCampaign = (campaign_id: string) => ({
  type: actionTypes.DELETE_CAMPAIGN,
  payload: campaign_id,
})

interface IGetCampaigns {
  from_ts: number
  to_ts: number
  utc_offset: number
  campaign_type: number
}

export interface ExternalClustersParams {
  rank_by: number
  competition_type: number
}

export interface ProductsClusterDataItem {
  item: string[]
  store_id: string[]
  from_ts: string
  to_ts: string
  app_from_ts: string
  app_to_ts: string
  baseline_ts: string
  title: string
  item_type: string
  parameter: string
  external_clusters_params: ExternalClustersParams
}

export interface CampaignFormValues {
  root_group_id: string | null
  products_cluster_data: ProductsClusterDataItem
  campaign_type: number
}

export interface CampaignModalProps {
  isOpen: boolean
  onClose: () => void
  onRequestClose: () => void
  onCreated: (data: CampaignFormValues) => void
  campaign?: CampaignFormValues | null
  campaignData?: CampaignData | null
}

export interface CampaignFormProps {
  onClose: () => void
  onCreated: (data: CampaignFormValues) => void
  campaign?: CampaignFormValues | null
}
export interface OverviewCampaignModalProps {
  isOpen: boolean
  onClose: () => void
  campaign: Campaign
  campaignData: CampaignData
}

export interface ProductsDetails {
  item_id: string[]
  segment: (string | null)[]
  product_description: (string | null)[]
  image_url: (string | null)[]
}

export interface Contexts {
  context_id: string[]
  context_name: string[]
  store_id: string[]
}

export interface CampaignData {
  products_details: ProductsDetails
  item_type: string
  contexts: Contexts
}

export interface FormRef {
  submit: () => Promise<void> | void
}

export const getRetailCampaigns =
  (params: IGetCampaigns): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { retailConfig } = getState().config
    const sub_retail_id = retailConfig?.sub_retail_id

    const payload = { ...params, ...(sub_retail_id ? { sub_retail_id } : {}) }
    try {
      dispatch(setCampaignsLoading(true))
      const response = await httpAuth.get(endpoints.retailCampaigns, { params: payload })
      dispatch(setCampaigns(response.data))
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(setCampaignsLoading(false))
    }
  }

export const createCampaign =
  (params: CampaignFormValues): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    const utc_offset = getUTCOffset()

    const products_cluster_data = {
      ...params.products_cluster_data,
      utc_offset,
    }
    const payload = {
      ...params,
      products_cluster_data: [products_cluster_data],
    }
    try {
      dispatch(setIsCampaignCreating(true))
      await httpAuth.post(endpoints.campaignsByGroup, { ...payload })
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      dispatch(setIsCampaignCreating(false))
    }
  }

interface IUpdateCampaignStatus {
  campaign_id: string
  status: number
}

export const updateCampaignStatus =
  (params: IUpdateCampaignStatus): ThunkAction<Promise<boolean>, RootState, unknown, AnyAction> =>
  async (dispatch, getState) => {
    const { campaigns } = getState().campaigns
    try {
      const payload = {
        campaign_id: params.campaign_id,
        status: params.status,
      }
      const res = await httpAuth.put(endpoints.updateCampaignStatus, payload)
      if (res.status === 200) {
        const existing = campaigns.find((c) => c.oid === params.campaign_id)
        if (existing) {
          const updatedCampaign = {
            ...existing,
            status: params.status,
          }
          dispatch(updateCampaign(updatedCampaign))
        }
        return true
      }
      return false
    } catch (error) {
      console.error(error)
      return false
    }
  }

export const deleteCampaignAction =
  (campaign_id: string): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    const payload = { campaign_id }
    const res = await httpAuth.delete(endpoints.campaign, { params: payload })
    if (res.status === 200) {
      dispatch(deleteCampaign(campaign_id))
    } else {
      throw new Error('Deletion failed')
    }
  }

interface IGetCampaignData {
  campaign_id: string
  return_products_only: boolean
}

export const getCampaignData =
  ({
    campaign_id,
    return_products_only,
  }: IGetCampaignData): ThunkAction<Promise<CampaignData | undefined>, RootState, unknown, AnyAction> =>
  async (_, getState) => {
    const { activeGroupID, retailConfig } = getState().config
    const rid = retailConfig?.sub_retail_id
    const params = {
      group: activeGroupID,
      campaign_id,
      sub_retail_id: rid,
      return_products_only,
    }
    try {
      const response = await httpAuth.get(endpoints.campaignData, { params })
      return response.data
    } catch (error) {
      console.error('Error fetching campaign data:', error)
      return undefined
    }
  }

export const getCampaignsByGroup =
  (): ThunkAction<Promise<Campaign[] | null>, RootState, unknown, AnyAction> =>
  async (dispatch: AppDispatch, getState) => {
    try {
      const { activeGroupID } = getState().config

      dispatch(setCampaignsLoading(true))
      const { data } = await httpAuth.get(endpoints.campaignsByGroup, {
        params: {
          group: activeGroupID,
        },
      })
      if (data) {
        dispatch(setCampaigns(data))
      }

      return data
    } catch (error) {
      console.error('Error fetching campaigns:', error)
      return null
    } finally {
      dispatch(setCampaignsLoading(false))
    }
  }

export const getCampaignById =
  (campaignId: string): ThunkAction<Promise<Campaign | null>, RootState, unknown, AnyAction> =>
  async (dispatch: AppDispatch, getState) => {
    const { campaigns } = getState().campaigns
    const campaign = campaigns.find((campaign) => campaign.oid === campaignId)

    if (campaign) {
      return campaign
    }
    const { data } = await httpAuth.get(endpoints.campaign, {
      params: {
        campaign_id: campaignId,
      },
    })
    if (data) {
      dispatch(setCampaigns([...campaigns, data]))
    }
    return data
  }

interface CampaignUrl {
  campaign: Campaign
  activeGroupID: string
  locale: string
  sub_retail_id?: string
}

export const getCampaignUrl = ({ campaign, activeGroupID, locale, sub_retail_id }: CampaignUrl) => {
  const token = getTokenFromCookie()
  const baseline_ts = campaign.baseline_ts
  const campaign_id = campaign.oid
  const currency_symbol = campaign.currency_symbol
  const data_from_ts = campaign.data_from_ts
  const data_to_ts = campaign.data_to_ts
  const to_ts = campaign.to_ts
  const from_ts = campaign.from_ts
  const group = activeGroupID
  const lang = locale
  const title = campaign.title
  const campaign_type = campaign.type

  let url = `${BASE_URL}lang=${lang}&token=${token}&group=${group}&campaign_id=${campaign_id}&baseline_ts=${baseline_ts}&data_from_ts=${data_from_ts}&data_to_ts=${data_to_ts}&to_ts=${to_ts}&from_ts=${from_ts}&title=${title}`

  if (sub_retail_id) {
    url += `&sub_retail_id=${sub_retail_id}`
  }

  if (currency_symbol) {
    url += `&currency_symbol=${currency_symbol}`
  }

  if (isNumber(campaign_type)) {
    url += `&campaign_type=${campaign_type}`
  }

  return url
}
